import { useEffect, useState } from 'react';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, MapPin, Pencil, Trash2, Check, Plus } from 'lucide-react';
import { EntityPicker } from './EntityPicker';
import { SyncBadge } from './SyncBadge';

// Project → Locations tab: every location in this project, how many scenes use it, rename/delete, import from other projects
export function ProjectLocations({ projectId }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [picker, setPicker] = useState(false);

  const load = () => api.get(`/projects/${projectId}/locations`).then(r => setLocations(Array.isArray(r.data) ? r.data : [])).catch(() => toast.error('Failed to load locations')).finally(() => setLoading(false));
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const rename = async (loc) => {
    if (!name.trim() || name.trim() === loc.name) { setEditing(null); return; }
    try { const res = await api.put(`/projects/${projectId}/locations/${loc.id}`, { name: name.trim() }); setLocations(prev => prev.map(l => l.id === loc.id ? { ...l, ...res.data } : l)); toast.success('Location renamed'); }
    catch { toast.error('Rename failed'); }
    finally { setEditing(null); }
  };
  const remove = async (loc) => {
    if (!window.confirm(`Delete "${loc.name}"? Scenes using it will lose their location link.`)) return;
    try { await api.delete(`/projects/${projectId}/locations/${loc.id}`); setLocations(prev => prev.filter(l => l.id !== loc.id)); toast.success('Location deleted'); }
    catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#8A8A93]" /></div>;
  return (
    <div data-testid="project-locations">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#8A8A93]">{locations.length} location{locations.length === 1 ? '' : 's'} in this project. Assign them to scenes in the Scene & Location step.</p>
        <button data-testid="import-location-btn" onClick={() => setPicker(true)} className="ff-btn-secondary text-xs flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Add from another project</button>
      </div>
      {locations.length === 0 ? (
        <div className="ff-card p-12 text-center"><MapPin className="w-10 h-10 text-[#55555C] mx-auto mb-3 opacity-40" /><p className="text-sm text-[#8A8A93]">No locations yet. Create one from a scene's Location step, or add one from another project.</p></div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {locations.map(loc => (
            <div key={loc.id} data-testid={`project-location-${loc.id}`} className="rounded-xl overflow-hidden border border-white/[0.06] bg-[#141418]">
              <div className="aspect-video bg-[#0a0a0e] relative">
                {loc.image_path ? <img src={getImageUrl(loc.image_path)} alt={loc.name} className="w-full h-full object-cover" /> : <MapPin className="w-8 h-8 text-[#55555C] absolute inset-0 m-auto" />}
              </div>
              <div className="p-3">
                {editing === loc.id ? (
                  <div className="flex gap-1">
                    <input data-testid={`location-rename-input-${loc.id}`} autoFocus value={name} onChange={e => setName(e.target.value)} onKeyDown={e => e.key === 'Enter' && rename(loc)} className="ff-input text-xs py-1 flex-1" />
                    <button onClick={() => rename(loc)} className="text-[#10B981] p-1"><Check className="w-3.5 h-3.5" /></button>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-white font-semibold truncate">{loc.name}</p>
                    <div className="flex gap-1 shrink-0">
                      <button data-testid={`location-rename-${loc.id}`} onClick={() => { setEditing(loc.id); setName(loc.name); }} className="text-[#8A8A93] hover:text-white p-0.5"><Pencil className="w-3 h-3" /></button>
                      <button data-testid={`location-delete-${loc.id}`} onClick={() => remove(loc)} className="text-[#8A8A93] hover:text-red-400 p-0.5"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </div>
                )}
                <p className="text-[10px] text-[#55555C] truncate mt-0.5">{loc.description || '—'}</p>
                <p className="text-[10px] text-[#8A8A93] mt-1 flex items-center gap-1.5 flex-wrap">Used in {loc.scene_count || 0} scene{loc.scene_count === 1 ? '' : 's'}{loc.origin_location_id && !loc.sync_group ? ' · copied' : ''}
                  <SyncBadge kind="location" entity={loc} onDetached={(u) => setLocations(prev => prev.map(l => l.id === u.id ? { ...l, ...u } : l))} /></p>
              </div>
            </div>
          ))}
        </div>
      )}
      {picker && <EntityPicker kind="location" projectId={projectId} excludeIds={locations.map(l => l.id)} onClose={() => setPicker(false)} title="Add a location to this project"
        onPick={(loc) => { setLocations(prev => prev.some(l => l.id === loc.id) ? prev : [...prev, { ...loc, scene_count: 0 }]); toast.success(`${loc.name} added`); }} />}
    </div>
  );
}
